<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import { InlineSearchBar } from '@/components/utilities/search';
import BuildingCanvasDialogModal from '@/components/dialogs/BuildingCanvasDialogModal.vue';
import DialogModal from '@/components/dialogs/DialogModal.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import ProjectRenderCanvasAccessible from '@/components/maps_canvases/ProjectRenderCanvasAccessible.vue';
import BuildingDatatable from '@/components/tables/BuildingDatatable.vue';
import BuildingList from '@/components/rental/buildings/BuildingList.vue';
import CanvasDataPanels from '@/components/utilities/CanvasDataPanels.vue';
import LoadingSpinner from '@/components/utilities/LoadingSpinner.vue';
import ViewModeToggleButton from '@/components/buttons/ViewModeToggleButton.vue';
import type { Region } from '@/typings/regions/Region';
import { useBuildingsApi } from '@/composables/useBuildingsApi';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useProjectsApi } from '@/composables/useProjectsApi';
import { usePurchaseFilters } from '@/composables/usePurchaseFilters';
import { type ListingTypeEnum, ListingTypeEnum as ListingTypeEnumValues, listingTypeEnumNumericValue } from '@/enums/ListingType';
import type { LocaleSchema } from '@/locales/index';
import type { Building } from '@/typings/buildings/Building';
import type { BuildingRenderLayer } from '@/typings/drawing/BuildingRenderLayer';
import type { Project } from '@/typings/projects/Project';
import type { GetBuildingsRequest } from '@/typings/requests/GetBuildingsRequest.ts';
import type { GetProjectRenderCanvasRequest } from '@/typings/requests/GetProjectRenderCanvasRequest.ts';
import type { Unit } from '@/typings/units/Unit';
import { DEFAULT_PURCHASE_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { DEFAULT_RENTAL_UNIT_STATUSES } from '@/constants/rentalConstants';
import { useBuilding } from '@/composables/useBuilding';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const { getProjectRenderCanvas, getProjectRenderCanvasLoading } = useProjectsApi();
const { getBuildingsByProjectId, getBuildingsByProjectIdLoading } = useBuildingsApi();
const { sortBuildingsByStatus } = useBuilding();

const { getRegionWizardUrlFromProject, getBuildingWizardUrl: getPurchaseBuildingWizardUrl } = usePurchaseLocalizedUrls();
const { getBuildingWizardUrl: getRentalBuildingWizardUrl } = useRentalLocalizedUrls();
const { loadFiltersFromPurchaseStorage } = usePurchaseFilters();

const props = defineProps<{
  unitOccupancyType: ListingTypeEnum;
  project: Project;
  region?: Region;
}>();

provide('app-header-height', 120);

// Mode d'affichage (cards ou table) -- toujours 'table' par défaut
const viewMode = ref<'cards' | 'table'>('table');

// Détection de la largeur d'écran pour afficher le bon bouton
const isDesktop = ref(window.innerWidth > 768);

const hoveredBuilding = ref<Building>();
const canvasIsLoaded = ref(false);
const buildingLayers = ref<BuildingRenderLayer[]>([]);
const buildings = ref<Building[]>([]);
const allBuildings = ref<Building[]>([]);
const rentalBuildings = ref<Building[]>([]);
const rentalBuildingLayers = ref<BuildingRenderLayer[]>([]);
const hoveredUnit = ref<Unit>();
const project = ref<Project>(props.project);
const wizardStep = ref(3);

// Modal state
const showBuildingModal = ref(false);
const modalBuilding = ref<Building>();

// Rental notification modal state
const showRentalNotificationModal = ref(false);
const rentalBuilding = ref<Building>();

const canvasIsReady = computed(() => {
  return buildingLayers.value.length > 0 && canvasIsLoaded.value;
});

const getDatatableTitle = computed(() => {
  return `${t('browsingApp.projects.label')} ${project.value?.localizedName}, ${project.value?.localizedRegionName}`;
});



function onCanvasLoaded() {
  canvasIsLoaded.value = true;
}

function onLayerClick(layer: BuildingRenderLayer) {
  const building = allBuildings.value.find(b => b.id === layer.id);
  if (building) {
    // Si c'est un bâtiment de location, afficher le popup de notification d'abord
    if (building.listingType === ListingTypeEnumValues.Rental) {
      rentalBuilding.value = building;
      showRentalNotificationModal.value = true;
      return;
    }
    
    // Sinon, ouvrir le modal pour les bâtiments en vente
    modalBuilding.value = building;
    showBuildingModal.value = true;
  }
}

function onBuildingSelected(building: Building) {
  // Navigation directe vers l'étape suivante
  window.location.href = getPurchaseBuildingWizardUrl(building);
}

function onBackToProjectSelection() {
  window.location.href = getRegionWizardUrlFromProject(props.project);
}

function resetDialogState() {
  modalBuilding.value = undefined;
}

// Computed pour obtenir l'ID de région du projet
const projectRegionId = computed(() => {
  // D'abord essayer depuis les props
  if (props.region?.id) {
    return props.region.id;
  }
  
  // Sinon, essayer de récupérer depuis les filtres stockés
  const filters = loadFiltersFromPurchaseStorage();
  return filters.regionId || null;
});

function onConfirmRentalRedirect() {
  if (rentalBuilding.value) {
    window.location.href = getRentalBuildingWizardUrl(rentalBuilding.value);
  }
}

function onCancelRentalRedirect() {
  showRentalNotificationModal.value = false;
  rentalBuilding.value = undefined;
}

// Fonction pour appliquer les filtres et recharger les bâtiments
async function applyFilters() {
  if (project.value) {
    try {
      const filters = loadFiltersFromPurchaseStorage();
      
      const purchaseRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_PURCHASE_UNIT_STATUSES,
        availabilityDates: filters.availabilityDates || undefined,
        floorNumbers: filters.floors,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        unitConfigurations:  filters.unitConfigurations.length > 0 ? filters.unitConfigurations : undefined
      };

      const purchaseCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale]
      };
      
      const [purchaseBuildingLayersData, filteredBuildings] = await Promise.all([
        getProjectRenderCanvas(project.value.id, purchaseCanvasRequest),
        getBuildingsByProjectId(project.value.id, purchaseRequest)
      ]);
      
      // Filtrer les couches de rendu pour ne montrer que les bâtiments avec unités disponibles
      const availableBuildingIds = filteredBuildings.map(b => b.id);
      const filteredPurchaseLayers = purchaseBuildingLayersData.filter(layer => availableBuildingIds.includes(layer.id));
      
      // Combiner les couches de ventes filtrées avec les couches de location (non filtrées)
      buildingLayers.value = [...filteredPurchaseLayers, ...rentalBuildingLayers.value];
      
      // Combiner les bâtiments de ventes filtrés avec les bâtiments de location (pour les clics sur le canvas)
      allBuildings.value = [...filteredBuildings, ...rentalBuildings.value];
      buildings.value = filteredBuildings;
    } catch (error) {
      console.error('Failed to reload buildings with filters:', error);
    }
  }
}

onMounted(async () => {
  if (project.value) {
    try {
      const saleRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_PURCHASE_UNIT_STATUSES
      };

      const rentalRequest: GetBuildingsRequest = {
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental],
        locale: locale.value.substring(0, 2),
        unitStatuses: DEFAULT_RENTAL_UNIT_STATUSES
      };

      const saleCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Sale]
      };

      const rentalCanvasRequest: GetProjectRenderCanvasRequest = {
        locale: locale.value.substring(0, 2),
        listingType: listingTypeEnumNumericValue[ListingTypeEnumValues.Rental]
      };

      const [saleBuildingLayersData, rentalBuildingLayersData, saleBuildingsData, rentalBuildingsData] = await Promise.all([
        getProjectRenderCanvas(project.value.id, saleCanvasRequest),
        getProjectRenderCanvas(project.value.id, rentalCanvasRequest),
        getBuildingsByProjectId(project.value.id, saleRequest),
        getBuildingsByProjectId(project.value.id, rentalRequest)
      ]);

      // Créer la liste de tous les IDs de bâtiments avec unités disponibles
      const availableBuildingIds = [...saleBuildingsData, ...rentalBuildingsData].map(b => b.id);

      const filteredRentalLayers = rentalBuildingLayersData.filter(layer => availableBuildingIds.includes(layer.id));

      // Stocker les bâtiments de vente séparément (ne changent pas avec les filtres)
      rentalBuildings.value = rentalBuildingsData;
      rentalBuildingLayers.value = filteredRentalLayers;
      
      // Combiner les couches de rendu pour afficher tous les bâtiments sur le canvas
      buildingLayers.value = [...saleBuildingLayersData, ...rentalBuildingLayersData];
      
      // Stocker tous les bâtiments pour le mapping des clics
      allBuildings.value = [...saleBuildingsData, ...rentalBuildingsData];
      
      // Afficher seulement les bâtiments en vente dans le datatable
      buildings.value = saleBuildingsData;
      
    } catch (error) {
      console.error('Failed to load project data:', error);
    }
  }

  // Écouter les changements de taille d'écran
  const handleResize = () => {
    isDesktop.value = window.innerWidth > 768;
  };
  window.addEventListener('resize', handleResize);
});
</script>

<template>
  <!--Achat - Étape 3 - Choix du bâtiment-->
  <div class="project-purchase-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                     :listing-type="ListingTypeEnumValues.Sale"
                     :selected-region="projectRegionId"
                     @filtersApplied="applyFilters" />
    <div class="wrapper wrapper_large">
      <UnitSelectionWizardSteps v-model="wizardStep" />
      <div class="main-content">
        <div class="heading-and-toggle">
          <!--En-tête avec bouton de retour-->
          <HeaderWithBackButton :title="`${getDatatableTitle}`"
                                :subtitle="t('browsingApp.buildings.chooseTheBuildingDescription')"
                                @back-click="onBackToProjectSelection">
            <template v-if="isDesktop" #actions>
              <!-- Toggle pour changer le mode d'affichage -->
              <ViewModeToggleButton v-model="viewMode" />
            </template>
          </HeaderWithBackButton>
        </div>
        <CanvasDataPanels>
          <template v-if="!getProjectRenderCanvasLoading"
                    #canvas>
            <template v-if="buildingLayers?.length">
              <ProjectRenderCanvasAccessible v-show="canvasIsReady"
                                             v-model:hovered-building="hoveredBuilding"
                                             :building-layers="buildingLayers"
                                             :buildings="allBuildings"
                                             :project="project"
                                             :hovered-unit="hoveredUnit"
                                             flow-type="purchase"
                                             @canvas-loaded="onCanvasLoaded"
                                             @building-click="onLayerClick" />
              <LoadingSpinner v-show="!canvasIsReady" />
            </template>
            <div v-else
                 class="placeholderNoInformation">
                {{ t('browsingApp.projects.noProjectCanvasData') }}
            </div>
          </template>
        
          <template #data>
            <div v-if="getBuildingsByProjectIdLoading"
                 class="browsingApp-loadingState">
              <LoadingSpinner />
            </div>

            <template v-else-if="buildings?.length">
              <!-- Mode cartes -->
              <BuildingList v-if="viewMode === 'cards'"
                            :buildings="sortBuildingsByStatus(buildings)"
                            :loading="getBuildingsByProjectIdLoading"
                            :is-selectable="true"
                            :mode="'purchase'"
                            @building-selected="onBuildingSelected" />

              <!-- Mode tableau -->
              <template v-else>
                <BuildingDatatable mode="purchase"
                                   v-model:hovered="hoveredBuilding"
                                   :buildings="sortBuildingsByStatus(buildings)"
                                   @building-click="onBuildingSelected"/>
              </template>

              <!-- Toggle pour changer le mode d'affichage (mobile) -->
              <ViewModeToggleButton v-if="!isDesktop"
                                    v-model="viewMode" />
            </template>
            <div v-else-if="!getBuildingsByProjectIdLoading"
                 class="placeholderNoInformation">
              {{ t('browsingApp.buildings.noBuildings') }}
            </div>
          </template>
        </CanvasDataPanels>

        <BuildingCanvasDialogModal v-model:open="showBuildingModal"
                                   :building="modalBuilding"
                                   @building-selected="onBuildingSelected"
                                   @reset-building-modal="resetDialogState" />

        <!-- Modal de notification pour les bâtiments de location -->
        <DialogModal v-model:open="showRentalNotificationModal"
                     :show-footer="true"
                     :confirm-button-label="t('browsingApp.actions.continueToRental')"
                     :cancel-button-label="t('browsingApp.actions.cancel')"
                     @confirm="onConfirmRentalRedirect"
                     @close="onCancelRentalRedirect">
          <template #headerContent>
            <h2>{{ t('browsingApp.rental.rentalBuildingNotice') }}</h2>
          </template>
          
          <div class="rentalNotification-content">
            <p>{{ t('browsingApp.rental.rentalRedirectMessage', { buildingNumber: rentalBuilding?.no }) }}</p>
          </div>
        </DialogModal>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.dataTable_units {
  .unitColumn {
    width: 10ch;
  }

  .bedroomsColumn {
    width: 8ch;
  }

  .floorColumn {
    width: 8ch;
  }

  .livingAreaColumn {
    width: 12ch;
  }

  .priceColumn {
    width: 12ch;
  }

  .statusColumn {
    width: 12ch;
  }
}

@media (width <= 520px) {
  .dataTable_units {
    .unitColumn {
      width: 8ch;
    }

    .bedroomsColumn {
      width: 6ch;
    }

    .floorColumn {
      width: 6ch;
    }

    .livingAreaColumn {
      width: 10ch;
    }

    .priceColumn {
      width: 10ch;
    }

    .statusColumn {
      width: 10ch;
    }
  }
}
</style>
